import { Injectable, OnDestroy, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AppGlobalsModule, AppGlobalsModule as _globals } from '../app-globals.module';
import { filter, first, map, share, shareReplay, switchMap, take } from 'rxjs/operators';
import { AngularApp } from '../../../src/serverTypes/yeshivaTypesDeclare';
import { BehaviorSubject, EMPTY, Observable, of, Subject } from 'rxjs';
import { UrlAddressService } from '../core/services/url-address/url-address.service';
import { UtilService } from '../core/services/util/util.service';

@Injectable()
export class SiteHeaderService implements OnDestroy {
    private http = inject(HttpClient);
    private _urlAddress = inject(UrlAddressService);

    private siteHeader$: Observable<AngularApp.API.SiteHeader> | undefined;

    constructor() {
        this._isMainMenuOpen$ = new BehaviorSubject<boolean>(false);
    }

    ngOnDestroy(): void {
        if (this._isMainMenuOpen$) {
            this._isMainMenuOpen$.unsubscribe();
        }
        if (this._headerMode$) {
            this._headerMode$.unsubscribe();
        }
    }

    private siteHeaderDataUrl = _globals.apiPath('siteheader');

    public getHeaderData() {
        if (!this.siteHeader$) {
            this.siteHeader$ = this.http.get<AngularApp.API.SiteHeader>(this.siteHeaderDataUrl).pipe(shareReplay({ bufferSize: 1, refCount: true }), first());
        }
        return this.siteHeader$;
    }

    private _headerMode$ = new Subject<HeaderMode>();
    public getHeaderMode$(): Observable<HeaderMode> {
        return this._headerMode$.asObservable().pipe(filter((headerMode) => headerMode !== undefined));
    }
    public setHeaderMode$(headerMode: HeaderMode): void {
        this._headerMode$.next(headerMode);
    }

    public getHeaderMode(isAtTop = true): HeaderMode {
        if (this._urlAddress.isDedicationDatePicker) {
            return HeaderMode.Iframe;
        }
        //no nihul-only compact lock here: the esi include (Esi.cs) renders every page static-mode,
        //so forcing Sticky on the client shifted the header after paint (CLS) - removed 2026-10-12
        return isAtTop ? HeaderMode.Static : HeaderMode.Sticky;
    }

    // ===========================
    // Belongs to both header and footer The appropriate place is in the wrapper that belongs to both
    private _isMainMenuOpen$: BehaviorSubject<boolean>;
    public getIsMainMenuOpen(): Observable<boolean> {
        return this._isMainMenuOpen$.asObservable();
    }
    public setIsMainMenuOpen(isMainMenuOpen: boolean): void {
        this._isMainMenuOpen$.next(isMainMenuOpen);
    }

    public getLogoSvg() {
        return this.getHeaderData().pipe(
            take(1),
            switchMap((data) => {
                // We need to bypass and create the SVG manually otherwise it wouldn't be possible to change it's color and size.
                if (data.SiteLogo) {
                    let logoPath = AppGlobalsModule.apiPath(data.SiteLogo, '', '');
                    return this.http.get(logoPath, { responseType: 'text' });
                } else {
                    // There is no logo so we return nothing (EMPTY) that complete the observable
                    return EMPTY;
                }
            }),
        );
    }
    //============================

    public getEmptyDailyDonor() {
        return $localize`:@@empty-daily-donor:הקדשת הלימוד באתר`;
    }
}

export interface BannerTop {
    txt: string;
    link: string;
    clr: string;
    bgClr: string;
}

export enum HeaderMode {
    // the string values are coupled - rename them together, in one angular + .net deploy:
    //   css: the .static-mode / .sticky-mode selectors in sticky-header.component.scss
    //   esi url: Esi.cs GetHeaderModeParam, and esi-factory.component.ts which maps the mode=
    //   query param to this enum; the cached esi fragments keep the old value until purged
    Static = 'static-mode', // in the page flow, at the top of every page
    Sticky = 'sticky-mode', // compact and fixed to the viewport once scrolled
    Iframe = 'iframe',
}
